import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    retryAttempts: 1,
    flowType: 'implicit',
    storageKey: 'supabase.auth.token',
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js-web',
    },
  },
  db: {
    schema: 'public',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// ── 監聽認證狀態變化 ─────────────────────────────────────────────
// ⚠️ 這裡只是被動 log，不代表「本 listener」主動清除了 session。
// SIGNED_OUT 事件的觸發來源可能是：
//   1. 使用者主動按登出（合法）
//   2. handleSupabaseError 攔截到 token 真正失效
//   3. Supabase 後端撤銷 token
// 若看到此 log，請對照上方 [SIGNOUT_CALLSITE] 記錄確認真正來源。
const isDevClient = import.meta.env.DEV;

supabase.auth.onAuthStateChange((event, session) => {
  if (!isDevClient) return;
  if (event === 'TOKEN_REFRESHED') {
    console.log('[AuthState] TOKEN_REFRESHED – uid:', session?.user?.id ?? 'none');
  } else if (event === 'SIGNED_OUT') {
    // ──────────────────────────────────────────────────────────────
    // ⚠️  此 SIGNED_OUT 為 Supabase SDK 的被動事件通知，
    //     本 listener 本身「沒有」呼叫 signOut()。
    //
    //  真正呼叫 signOut 的地方只有三處（標記為 [SIGNOUT_CALLSITE]）：
    //    1. AdminLayout/handleLogout – 使用者主動按登出
    //    2. Navbar/handleLogout      – 使用者主動按登出
    //    3. handleSupabaseError      – refresh_token 真正失效
    //
    //  若懷疑不該觸發，請搜尋 [SIGNOUT_CALLSITE] 對照呼叫來源。
    //  [AUTH_BLOCK] 標記的地方是「被攔截、未 signOut」的情境。
    // ──────────────────────────────────────────────────────────────
    console.warn('[AuthState] SIGNED_OUT event (passive) – session cleared by signOut elsewhere. Search [SIGNOUT_CALLSITE] for origin.');
  } else if (event === 'SIGNED_IN') {
    console.log('[AuthState] SIGNED_IN – uid:', session?.user?.id ?? 'none');
  } else if (event === 'USER_UPDATED') {
    console.log('[AuthState] USER_UPDATED – uid:', session?.user?.id ?? 'none');
  }
});

// ── Refresh Token 失效偵測 ─────────────────────────────────────────
/** 偵測 error 是否為 refresh token 真正失效（invalid_grant 等） */
export function isInvalidRefreshTokenError(error: any): boolean {
  if (!error) return false;
  const msg = String(error?.message || error?.error_description || error?.msg || '');
  const errorCode = String(error?.code || error?.error_code || '');
  return (
    msg.includes('invalid_grant') ||
    msg.includes('refresh_token not found') ||
    msg.includes('Refresh Token Not Found') ||
    msg.includes('Invalid Refresh Token') ||
    msg.includes('token has been revoked') ||
    msg.includes('Refresh token not found') ||
    errorCode === 'refresh_token_not_found' ||
    error?.code === 'refresh_token_not_found' ||
    error?.error === 'invalid_grant'
  );
}

// ── 徹底清除本地 Auth Storage ────────────────────────────────────
/** 清除所有與 Supabase/Admin auth 相關的 localStorage / sessionStorage */
export function clearSupabaseAuthStorage(): void {
  try {
    // Supabase auth keys
    localStorage.removeItem('supabase.auth.token');
    sessionStorage.removeItem('supabase.auth.token');

    // sb-* auth keys (Supabase auto-prefixed)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith('sb-')) keysToRemove.push(k);
    }
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith('sb-')) keysToRemove.push(k);
    }
    keysToRemove.forEach(k => {
      try { localStorage.removeItem(k); } catch { /* ignore */ }
      try { sessionStorage.removeItem(k); } catch { /* ignore */ }
    });

    // Admin locals
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminId');
    localStorage.removeItem('adminName');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminLocationId');

    // Admin cache
    sessionStorage.removeItem('admin_profile_cache');
  } catch {
    // storage access denied (e.g. private mode) → ignore
  }
}

// ── 全域錯誤處理函數 ─────────────────────────────────────────────
// ⚠️ 重要原則：
//   - 只有在 refresh_token 真正失效（invalid_grant）時才可呼叫 signOut
//   - 一般 JWT 驗證失敗（如 RLS 拒絕）不可 signOut，那只是權限問題不是 token 失效
//   - 'Failed to fetch' 是網路問題，絕對不可 signOut
export const handleSupabaseError = (error: any) => {
  if (isDevClient) console.error('Supabase error:', error);

  const isRefreshTokenInvalid = isInvalidRefreshTokenError(error);

  if (isRefreshTokenInvalid) {
    if (isDevClient) console.warn('[SIGNOUT_CALLSITE] handleSupabaseError – refresh_token truly invalid, clearing session. LEGAL.');
    clearSupabaseAuthStorage();

    const isAdminPage = window.location.pathname.startsWith('/admin');
    const loginPath = isAdminPage ? '/admin/login' : '/account/login';

    if (!window.location.pathname.includes('/login')) {
      window.location.href = loginPath;
    }
    return error;
  }

  // ⚠️ 以下情況「不可」signOut：
  //   - 含 'JWT' 字眼的 RLS 拒絕錯誤
  //   - 'Failed to fetch'（純網路問題）
  //   - 一般 permission denied
  // 這些只是查詢失敗，不代表 session 失效，不需要登出使用者
  if (error?.message?.includes('JWT') || error?.message?.includes('Failed to fetch')) {
    if (isDevClient) console.warn('[handleSupabaseError] Non-fatal auth-related error – NOT signing out. message:', error.message);
  }

  return error;
};

// ── 全域未處理 Promise Rejection 攔截器 ───────────────────────────
// 用來攔截 Supabase autoRefreshToken 背景刷新失敗的錯誤
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  if (isInvalidRefreshTokenError(error)) {
    if (isDevClient) console.warn('[unhandledrejection] Caught invalid refresh token error. Clearing session.');
    clearSupabaseAuthStorage();
    event.preventDefault();

    const isAdminPage = window.location.pathname.startsWith('/admin');
    const loginPath = isAdminPage ? '/admin/login' : '/account/login';
    if (!window.location.pathname.includes('/login')) {
      window.location.href = loginPath;
    }
  }
});

// ── 帶錯誤處理的 Supabase 呼叫包裝器 ─────────────────────────────────────
/** 包裝 async 函數，自動攔截並處理 invalid refresh token 錯誤 */
export function withAuthErrorHandling<T>(fn: () => Promise<T>): Promise<T> {
  return fn().catch((error) => {
    if (isInvalidRefreshTokenError(error)) {
      handleSupabaseError(error);
      throw new Error('Session expired. Please sign in again.');
    }
    throw error;
  });
}